// Alliance panel — replaces the in-game bot panel in the middle-column
// top slot. Lists every other player currently in the room with their
// callsign and a one-click ally/unally toggle. Status indicators:
//   • green pip — active mutual pact (both sides want each other)
//   • amber pip — one-way: either I asked and they haven't, or vice versa
//   • grey pip  — neither side wants
//
// Wires to the existing alliance protocol (c2s.requestAlly /
// c2s.cancelAlly). Bots show up as ordinary players because they own a
// tank slot just like humans do.
//
// Layout follows Variant A of the 2026-05-10 Claude Design handoff
// (`~Internal/Bolo2K Design System - aliance panel.zip`): a header band
// with title + on-field counter, the local player pinned at the top with
// a "YOU" tag, then one row per other player. The Mac System 7 outer
// chrome (`.bolo-panel + .bolo-panel-inner`) is kept; only the inner
// composition + state semantics come from the handoff.
//
// Spec rationale: the original Mac Bolo had a pull-down menu for offering
// alliances to other players (Cheshire's Brain.h `wantallies` bitmap is
// the underlying state). Replacing the in-game bot widget with this panel
// is the closer match to the original — bot management moved to the lobby
// per the one-Bolo-per-brain spec.

import type { Connection } from '@bolo2k/client-core';
import type { ClientState } from '@bolo2k/client-core';
import { playerLabel } from '@bolo2k/client-core';

interface Row {
  li: HTMLLIElement;
  pip: HTMLSpanElement;
  label: HTMLSpanElement;
  toggle: HTMLButtonElement;
  toggleLbl: HTMLSpanElement;
  toggleHov: HTMLSpanElement;
  owner: number;
}

export class AlliancePanel {
  private list: HTMLUListElement;
  private countChip: HTMLSpanElement;
  private youRow: HTMLLIElement;
  private youName: HTMLSpanElement;
  private rows = new Map<number, Row>();
  private conn: Connection | null = null;
  private lastSig = '';
  // Snapshot-version gate: everything the signature reads (tanks, names,
  // alliance bitmaps) only changes when a new snapshot lands, so skip even
  // the signature build (Set/sort/map/join allocations) on unchanged frames.
  private lastVersion = -1;

  constructor(private readonly root: HTMLElement) {
    root.innerHTML = '';

    const header = document.createElement('div');
    header.className = 'ally-header';
    const title = document.createElement('span');
    title.className = 'ally-title';
    title.textContent = 'Alliances';
    this.countChip = document.createElement('span');
    this.countChip.className = 'ally-count';
    header.append(title, this.countChip);
    root.appendChild(header);

    this.list = document.createElement('ul');
    this.list.className = 'ally-list';
    root.appendChild(this.list);

    // YOU row — always first when we have a tank. Built once and shown/
    // hidden via .hidden so the DOM never reorders.
    this.youRow = document.createElement('li');
    this.youRow.className = 'ally-row ally-row-you';
    const youPip = document.createElement('span');
    youPip.className = 'ally-pip ally-pip-you';
    this.youName = document.createElement('span');
    this.youName.className = 'ally-name';
    this.youRow.append(youPip, this.youName);
  }

  attachConnection(conn: Connection): void {
    this.conn = conn;
  }

  /** Re-render rows. Cheap signature check skips the DOM walk when nothing
   *  changed, which is most ticks. */
  draw(state: ClientState): void {
    if (state.version === this.lastVersion) return;
    this.lastVersion = state.version;
    if (state.myOwner < 0) {
      // Spectators don't ally with anyone — collapse the list.
      if (this.list.children.length === 0) return;
      this.list.innerHTML = '';
      this.rows.clear();
      this.youRow.remove();
      this.countChip.textContent = '';
      this.lastSig = '';
      return;
    }
    // Other-player roster: anyone with a tank in the snapshot, excluding me.
    const others = Array.from(new Set(state.tanks.map((t) => t.owner)))
      .filter((o) => o !== state.myOwner && o >= 0)
      .sort((a, b) => a - b);
    const myWants = state.wantAllies[state.myOwner] ?? 0;
    const myActive = state.allies[state.myOwner] ?? 0;

    const myLabel = playerLabel(state, state.myOwner);
    const sig =
      `me:${myLabel}|` +
      others
        .map((o) => {
          const wantsMe = ((state.wantAllies[o] ?? 0) & (1 << state.myOwner)) !== 0;
          const iWant = (myWants & (1 << o)) !== 0;
          const active = (myActive & (1 << o)) !== 0;
          return `${o}:${state.playerNames[o] ?? ''}:${active ? 'A' : iWant && wantsMe ? 'A' : iWant ? 'P' : wantsMe ? 'I' : '-'}`;
        })
        .join('|');
    if (sig === this.lastSig) return;
    this.lastSig = sig;

    // Header chip: total field count (me + others, including bots).
    const fieldCount = others.length + 1;
    this.countChip.textContent = `${fieldCount} on field`;

    // Ensure YOU row is in the list at index 0 and up to date.
    if (this.youRow.parentNode !== this.list) {
      this.list.prepend(this.youRow);
    }
    this.youName.textContent = '';
    const nameText = document.createTextNode(myLabel);
    const youTag = document.createElement('span');
    youTag.className = 'ally-you-tag';
    youTag.textContent = 'YOU';
    this.youName.append(nameText, youTag);

    // Drop rows for players who left.
    for (const [owner, row] of this.rows) {
      if (!others.includes(owner)) {
        row.li.remove();
        this.rows.delete(owner);
      }
    }

    // Empty placeholder when nobody else is on the field.
    const placeholder = this.list.querySelector('.ally-empty');
    if (others.length === 0) {
      if (!placeholder) {
        const empty = document.createElement('li');
        empty.className = 'ally-empty';
        empty.textContent = '(no other players)';
        this.list.appendChild(empty);
      }
      return;
    }
    if (placeholder) placeholder.remove();

    for (const owner of others) {
      let row = this.rows.get(owner);
      if (!row) row = this.makeRow(owner);
      const wantsMe = ((state.wantAllies[owner] ?? 0) & (1 << state.myOwner)) !== 0;
      const iWant = (myWants & (1 << owner)) !== 0;
      const active = (myActive & (1 << owner)) !== 0;

      row.label.textContent = playerLabel(state, owner);

      // Reset modifier classes — `ally-toggle` itself is preserved as the
      // base class.
      row.pip.className = 'ally-pip';
      row.toggle.className = 'ally-toggle';
      if (active) {
        row.pip.classList.add('active');
        row.toggle.classList.add('allied');
        row.toggleLbl.textContent = 'ALLIED';
        row.toggleHov.textContent = 'LEAVE';
        row.toggle.title = 'click to break this alliance';
      } else if (iWant && wantsMe) {
        // Edge case: bitmaps disagree (race condition); treat as active.
        row.pip.classList.add('active');
        row.toggle.classList.add('allied');
        row.toggleLbl.textContent = 'ALLIED';
        row.toggleHov.textContent = 'LEAVE';
        row.toggle.title = 'click to break this alliance';
      } else if (iWant) {
        row.pip.classList.add('pending');
        row.toggle.classList.add('waiting');
        row.toggleLbl.textContent = 'WAITING';
        row.toggleHov.textContent = 'CANCEL';
        row.toggle.title = 'you asked — waiting for them to confirm; click to cancel';
      } else if (wantsMe) {
        row.pip.classList.add('pending');
        row.toggle.classList.add('accept');
        row.toggleLbl.textContent = 'ACCEPT';
        row.toggleHov.textContent = 'ACCEPT';
        row.toggle.title = `${playerLabel(state, owner)} wants to ally with you — click to accept`;
      } else {
        row.toggleLbl.textContent = 'ALLY';
        row.toggleHov.textContent = 'ALLY';
        row.toggle.title = 'request alliance';
      }
    }
  }

  private makeRow(owner: number): Row {
    const li = document.createElement('li');
    li.className = 'ally-row';
    const pip = document.createElement('span');
    pip.className = 'ally-pip';
    const label = document.createElement('span');
    label.className = 'ally-name';
    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'ally-toggle';
    const toggleLbl = document.createElement('span');
    toggleLbl.className = 'ally-toggle-lbl';
    const toggleHov = document.createElement('span');
    toggleHov.className = 'ally-toggle-hov';
    toggle.append(toggleLbl, toggleHov);
    toggle.addEventListener('click', () => {
      // Active or waiting → cancel. Neutral or accept → request (which
      // also accepts an inbound request because requestAlly is idempotent
      // and mutual interest auto-resolves to active server-side).
      const isCancel =
        toggle.classList.contains('allied') || toggle.classList.contains('waiting');
      if (isCancel) this.conn?.cancelAlly(owner);
      else this.conn?.requestAlly(owner);
    });
    li.append(pip, label, toggle);
    this.list.appendChild(li);
    const row: Row = { li, pip, label, toggle, toggleLbl, toggleHov, owner };
    this.rows.set(owner, row);
    return row;
  }
}
